import { decode } from '@aytm/app/insights/dashboard/lib/sanitize'
import type { AttributeType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
export type MetricsType = {
  name: string
  id: string
  type: string
}

export type DisplayedAttributeType = {
  type: 'attribute'
  attribute: string
  section: string
  metrics: MetricsType
  selectedMetrics: string[]
  metricsOptions?: MetricsType[]
  showMetrics: undefined | boolean
  defaultMetrics: MetricsType
  disabled: string[]
}

export type SectionType = {
  type: 'section'
  collapsed: boolean
  section: string
  target: string
}

export type CaptionsList = (DisplayedAttributeType | SectionType)[]

export type GroupedOptionsList = {
  label: string
  code: string
  items: { label: string; code: string }[]
  groupBy?: string
}[]

export function makeGroupedAttributeOptionList(
  attributes: Record<string, AttributeType>,
  source: CaptionsList,
  isManual?: boolean
): GroupedOptionsList {
  const intermediateResult = source
    // The first run establishes groups
    .reduce(
      (
        memo, // : [Map<string, GroupedOptionsList[number]>, string],
        { type, ...rest } //: [Map<string, GroupedOptionsList[number]>, string]
      ) => {
        if ('section' === type) {
          const { target } = rest as SectionType
          return [memo[0], target] as const
        }

        const [map, target] = memo
        const { attribute: attributeKey, metrics, defaultMetrics } = rest as DisplayedAttributeType
        const attributeText = attributes?.[attributeKey]?.text || attributeKey

        const metricsName = (metrics ?? defaultMetrics)?.name ?? ''
        const metricsId = (metrics ?? defaultMetrics)?.id ?? ''

        if (!map.has(attributeKey)) {
          map.set(attributeKey, { label: decode(attributeText), code: attributeKey, items: [], groupBy: target })
        }
        const { label, code, items, groupBy } = map.get(attributeKey)
        return metricsName
          ? ([
              map.set(attributeKey, {
                label,
                code,
                items: items.some(item => item.code === `${attributeKey}|${metricsId}`)
                  ? items
                  : [...items, { label: metricsName, code: `${attributeKey}|${metricsId}` }],
                groupBy
              }),
              target
            ] as const)
          : memo
      },
      // we need this functionality
      [new window.Map<string, GroupedOptionsList[number]>(), null as string] as const
    )[0]

  // the second run eliminates empty groups - it is either metadata - it groups all metadata, or - rest - it groups all attributes that are not in any group
  const result = Array.from(intermediateResult.values())
    .reduce(
      (map, { label, code, items, groupBy }) => {
        if ('meta' === groupBy) {
          if (!map.has(groupBy)) {
            map.set(groupBy, { label: 'Metadata', code: 'metadata', items: [] })
          }
          const item = map.get(groupBy)
          return items.some(item => item.code === code)
            ? map
            : map.set(groupBy, { ...item, items: [...item.items, { label, code }] })
        } else if (!items.length) {
          if (!map.has('empty')) {
            map.set('empty', { label: 'Attributes', code: 'rest', items: [] })
          }
          const item = map.get('empty')
          return items.some(item => item.code === code)
            ? map
            : map.set('empty', { ...item, items: [...item.items, { label, code }] })
        }
        return map.set(code, { label, code, items })
      },
      // we need this functionality
      new window.Map<string, GroupedOptionsList[number]>()
    )
    .values()

  const options = [
    {
      label: '',
      code: '',
      items: [{ label: 'None', code: 'none' }].concat(isManual ? [{ label: 'Manual', code: '--manual--' }] : [])
    },
    ...result
  ]
  return options as GroupedOptionsList
}

// TC-IP-EDIT.23 / TC-IP-EDIT.24 — SubjectSelector disabled guard
export function isSubjectSelectorDisabled(params: {
  isSharedView: boolean
  editing: boolean
  isSplitOn: boolean
  isTrackEntity: boolean
}): boolean {
  return params.isSharedView || params.editing || params.isSplitOn || !params.isTrackEntity
}

// TC-IP-EDIT.23 — missedSubjects 'add' entry guard
export function shouldIncludeAddEntry(params: {
  editing: boolean
  isSharedView: boolean
  canUpdateDashboard: boolean
}): boolean {
  return !params.editing && !params.isSharedView && params.canUpdateDashboard
}

// TC-IP-SORT.24 / TC-IP-SORT.25 — Rank-by target derivation
export function deriveRankByTarget(groupCode?: string | null): 'meta' | 'attribute' {
  return groupCode === 'metadata' ? 'meta' : 'attribute'
}
